import * as THREE from 'three';

export type PreviewViewId =
  | 'top'
  | 'side'
  | 'angle-a'
  | 'angle-b'
  | 'downhill'
  | 'uphill';

export interface PreviewView {
  readonly id: PreviewViewId;
  readonly label: string;
  readonly direction: readonly [number, number, number];
  readonly target?: readonly [number, number, number];
  readonly up?: readonly [number, number, number];
  readonly projection?: 'orthographic' | 'perspective';
  readonly distance?: number;
  readonly span?: number;
  readonly fov?: number;
}

export interface PreviewRendererOptions {
  readonly views: readonly PreviewView[];
  readonly bounds?: THREE.Box3;
  readonly background?: THREE.ColorRepresentation;
  readonly captureMode?: boolean;
}

function finiteBox(box: THREE.Box3): boolean {
  return Number.isFinite(box.min.x)
    && Number.isFinite(box.min.y)
    && Number.isFinite(box.min.z)
    && Number.isFinite(box.max.x)
    && Number.isFinite(box.max.y)
    && Number.isFinite(box.max.z);
}

/** Renders several deterministic camera angles into one WebGL canvas. */
export class PreviewRenderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly cells: readonly HTMLElement[];
  private readonly resizeObserver: ResizeObserver;
  private readonly center = new THREE.Vector3();
  private readonly radius: number;
  private readonly bounds: THREE.Box3;
  private frame = 0;
  private disposed = false;

  constructor(
    private readonly stage: HTMLElement,
    private readonly scene: THREE.Scene,
    private readonly options: PreviewRendererOptions,
  ) {
    this.cells = options.views.map((view) => {
      const cell = document.createElement('section');
      cell.className = 'preview-cell';
      cell.dataset.view = view.id;
      const label = document.createElement('h2');
      label.className = 'preview-label';
      label.textContent = view.label;
      cell.append(label);
      return cell;
    });

    const grid = document.createElement('div');
    grid.className = 'preview-grid';
    grid.append(...this.cells);
    stage.append(grid);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: options.captureMode ?? false,
    });
    this.renderer.domElement.className = 'gallery-renderer';
    this.renderer.setPixelRatio(options.captureMode ? 1 : Math.min(window.devicePixelRatio, 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.setClearColor(options.background ?? 0xc6dee7);
    stage.prepend(this.renderer.domElement);

    const box = options.bounds?.clone() ?? new THREE.Box3();
    this.bounds = box;
    if (finiteBox(box) && !box.isEmpty()) {
      box.getCenter(this.center);
      const sphere = new THREE.Sphere();
      box.getBoundingSphere(sphere);
      this.radius = Math.max(sphere.radius, 0.5);
    } else {
      this.radius = 10;
    }

    this.resizeObserver = new ResizeObserver(() => this.requestRender());
    this.resizeObserver.observe(stage);
    this.requestRender();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.frame);
    this.resizeObserver.disconnect();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private requestRender(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.render());
  }

  private render(): void {
    if (this.disposed) return;
    const stageRect = this.stage.getBoundingClientRect();
    const width = Math.round(stageRect.width);
    const height = Math.round(stageRect.height);
    if (width < 1 || height < 1) return;

    this.renderer.setSize(width, height, false);
    this.renderer.setScissorTest(false);
    this.renderer.clear();
    this.renderer.setScissorTest(true);

    this.options.views.forEach((view, index) => {
      const cell = this.cells[index];
      if (!cell) return;
      const rect = cell.getBoundingClientRect();
      const cellWidth = Math.max(1, rect.width);
      const cellHeight = Math.max(1, rect.height);
      const left = rect.left - stageRect.left;
      const bottom = stageRect.bottom - rect.bottom;
      const camera = this.createCamera(view, cellWidth / cellHeight);

      this.renderer.setViewport(left, bottom, cellWidth, cellHeight);
      this.renderer.setScissor(left, bottom, cellWidth, cellHeight);
      this.renderer.render(this.scene, camera);
    });

    this.renderer.setScissorTest(false);
    document.documentElement.dataset.galleryReady = 'true';
    window.dispatchEvent(new Event('gallery-ready'));
  }

  private createCamera(view: PreviewView, aspect: number): THREE.Camera {
    const target = view.target
      ? new THREE.Vector3(...view.target)
      : this.center.clone();
    const direction = new THREE.Vector3(...view.direction).normalize();
    const distance = view.distance ?? this.radius * 4.2;
    const position = target.clone().addScaledVector(direction, distance);
    const span = view.span ?? this.radius * 1.3;
    let camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

    if (view.projection === 'perspective') {
      camera = new THREE.PerspectiveCamera(view.fov ?? 42, aspect, Math.max(0.05, distance / 100), distance * 4);
    } else {
      camera = new THREE.OrthographicCamera(-span * aspect, span * aspect, span, -span, 0.05, distance * 4);
    }

    camera.position.copy(position);
    camera.up.set(...(view.up ?? [0, 1, 0]));
    camera.lookAt(target);
    camera.updateMatrixWorld();
    if (camera instanceof THREE.OrthographicCamera && view.span === undefined) {
      this.fitOrthographicCamera(camera, aspect);
    }
    camera.updateProjectionMatrix();
    return camera;
  }

  private fitOrthographicCamera(camera: THREE.OrthographicCamera, aspect: number): void {
    if (this.bounds.isEmpty() || !finiteBox(this.bounds)) return;
    let halfWidth = 0;
    let halfHeight = 0;
    const corner = new THREE.Vector3();

    for (const x of [this.bounds.min.x, this.bounds.max.x]) {
      for (const y of [this.bounds.min.y, this.bounds.max.y]) {
        for (const z of [this.bounds.min.z, this.bounds.max.z]) {
          corner.set(x, y, z).applyMatrix4(camera.matrixWorldInverse);
          halfWidth = Math.max(halfWidth, Math.abs(corner.x));
          halfHeight = Math.max(halfHeight, Math.abs(corner.y));
        }
      }
    }

    const padding = 1.2;
    halfWidth = Math.max(halfWidth * padding, 0.5);
    halfHeight = Math.max(halfHeight * padding, 0.5);
    if (halfWidth / halfHeight > aspect) {
      halfHeight = halfWidth / aspect;
    } else {
      halfWidth = halfHeight * aspect;
    }
    camera.left = -halfWidth;
    camera.right = halfWidth;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;
  }
}

export const MODEL_VIEWS: readonly PreviewView[] = [
  { id: 'top', label: 'Top', direction: [0, 1, 0], up: [0, 0, -1], span: undefined },
  { id: 'side', label: 'Side', direction: [0, 0.12, 1], up: [0, 1, 0] },
  { id: 'angle-a', label: 'Front angle', direction: [1, 0.72, 1] },
  { id: 'angle-b', label: 'Back angle', direction: [-1, 0.64, -1] },
];

export const ENVIRONMENT_VIEWS: readonly PreviewView[] = [
  {
    id: 'top',
    label: 'Top',
    direction: [0, 1, 0],
    target: [0, 0, 1],
    up: [0, 0, -1],
    distance: 155,
    span: 58,
  },
  {
    id: 'side',
    label: 'Side',
    direction: [1, 0.34, 0.08],
    target: [0, -2, -8],
    projection: 'perspective',
    distance: 105,
    fov: 43,
  },
  {
    id: 'downhill',
    label: 'Downhill',
    direction: [0, 0.4, 1],
    target: [0, -5, -24],
    projection: 'perspective',
    distance: 88,
    fov: 46,
  },
  {
    id: 'uphill',
    label: 'Uphill',
    direction: [0, 0.24, -1],
    target: [0, 7, 25],
    projection: 'perspective',
    distance: 92,
    fov: 46,
  },
];
